/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/main.tsx
 * layer: web
 * area: main.tsx
 * kind: source
 * web_root: main.tsx
 * relpath: web/src/main.tsx
 * filename: main.tsx
 * extension: tsx
 * ---
 * hyperion-index:end
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { App } from './app/App';
import { initErrorTelemetry } from '@/platform/observability/error-telemetry';
import { initWebVitals } from './platform/observability/web-vitals';
import { initDebugLogger } from '@/platform/observability/debug-logger';
import './index.css';

// Initialise frontend error telemetry before rendering
initErrorTelemetry();
initWebVitals();

// Debug logger — enable with ?debug=true or Ctrl+Shift+L
// Captures ALL API calls, clicks, navigations, errors to downloadable log
initDebugLogger();

// ── Stale-bundle recovery ──────────────────────────────────────────────────
// When we deploy, Vite emits new hashed chunk filenames and the old ones are
// removed from disk. A browser holding an OLD index.html still references
// OLD chunk hashes; any lazy-loaded page tries to fetch them, gets 404, and
// the app fails with "Failed to fetch dynamically imported module".
//
// Vite dispatches `vite:preloadError` for exactly this case. We reload once
// to pick up the new index.html (and its new chunk hashes).
//
// Guarded by a sessionStorage flag so we don't loop if the reload ALSO
// fails — better to let the user see the error than auto-reload forever.
const RELOAD_FLAG = 'hyperion:stale-bundle-reloaded';
window.addEventListener('vite:preloadError', (event) => {

  console.warn('[stale-bundle] preload error — reloading to pick up new index.html', event);
  try {
    if (window.sessionStorage.getItem(RELOAD_FLAG)) {
      // Already reloaded once this session — don't loop.
      return;
    }
    window.sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    // sessionStorage unavailable — reload anyway.
  }
  // Prevent the default (which would just log) and reload hard.
  event.preventDefault?.();
  window.location.reload();
});

// Clear the reload flag once the current page has been stable for a bit.
// After 60s of successful operation we assume the reload recovered; clearing
// the flag lets a subsequent deploy trigger its own reload.
window.addEventListener('load', () => {
  window.setTimeout(() => {
    try {
      window.sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
      // ignore
    }
  }, 60_000);
});

// Register service worker for push notifications
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      console.warn('[SW] Registration failed:', err);
    });
  });
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Hyperion application root element is missing');
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
